import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import ReactJsonView, { InteractionProps } from "react-json-view";
import merge from "lodash/merge";
import {
  ConfigType,
  EmbedConfigContext,
  defaultConfig,
} from "../EmbedConfigContext";
import { Button, Drawer, Tabs, Tooltip, message } from "antd";
import SchemaForm, { SchemaFormSection } from "./SchemaForm";
import { playgroundSectionSchemas, schemaSkeleton, sdkSectionSchemas } from "../sdkSchema";
import { isInternalMode } from "../isInternal";
import { CopyOutlined, ReloadOutlined } from "@ant-design/icons";

const showPrivate = isInternalMode();

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

const SettingsDrawer = ({ open, onClose }: SettingsDrawerProps) => {
  const sections = useMemo<SchemaFormSection[]>(() => {
    const sectionSchemas: Record<string, SchemaFormSection["schema"]> = {
      ...playgroundSectionSchemas,
      ...sdkSectionSchemas,
    };
    return Object.keys(defaultConfig).map((key) => ({ key, schema: sectionSchemas[key] }));
  }, []);
  const configSkeleton = useMemo(() => Object.fromEntries(
    sections.map(({ key, schema }) => [key, schemaSkeleton(schema, showPrivate)])
  ), [sections]);
  const { config, setConfig } = useContext(EmbedConfigContext);
  const [draftConfig, setDraftConfig] = useState<ConfigType>(config);

  useLayoutEffect(() => {
    setDraftConfig(config);
  }, [config]);

  const handleReset = useCallback(() => setConfig(defaultConfig), []);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(draftConfig, null, 2));
    message.success("Copied to clipboard");
  }, [draftConfig]);

  const handleJsonUpdate = useCallback(
    (props: InteractionProps) =>
      setDraftConfig(props.updated_src as ConfigType),
    []
  );
  const handlePublish = useCallback(() => {
    setConfig(draftConfig);
    message.success("Sucessfully published");
  }, [draftConfig]);
  const handleCancel = useCallback(() => setDraftConfig(config), [config]);

  const jsonViewSrc = useMemo(() => merge({}, configSkeleton, draftConfig), [configSkeleton, draftConfig]);

  const isEditing = useMemo(
    () => config !== draftConfig,
    [config, draftConfig]
  );

  return (
    <Drawer
      title={
        <div
          style={{
            display: "inline-flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Settings{" "}
          <div
            style={{
              display: "inline-flex",
              columnGap: "20px",
              marginRight: 30,
            }}
          >
            <Tooltip title="Reset to default" placement="top">
              <ReloadOutlined onClick={handleReset} />
            </Tooltip>
            <Tooltip title="Copy as JSON" placement="top">
              <CopyOutlined onClick={handleCopy} />
            </Tooltip>
          </div>
        </div>
      }
      mask={false}
      placement="right"
      onClose={onClose}
      open={open}
      width={500}
    >
      <Tabs
        defaultActiveKey="form"
        items={[
          {
            key: "form",
            label: "Form",
            children: (
              <SchemaForm
                sections={sections}
                value={draftConfig as unknown as Record<string, Record<string, unknown>>}
                onChange={(value) => setDraftConfig(value as unknown as ConfigType)}
                showPrivate={showPrivate}
              />
            ),
          },
          {
            key: "json",
            label: "JSON",
            children: (
              <ReactJsonView
                name="Configurable Props"
                collapsed={1}
                defaultValue={undefined}
                displayDataTypes
                displayObjectSize={false}
                onAdd={handleJsonUpdate}
                onDelete={handleJsonUpdate}
                onEdit={handleJsonUpdate}
                src={jsonViewSrc}
                validationMessage=""
              />
            ),
          },
        ]}
      />
      {isEditing && (
        <div
          style={{
            display: "inline-flex",
            columnGap: 25,
            marginTop: 20,
            float: "right",
            marginRight: 30,
          }}
        >
          <Button size="middle" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="middle" onClick={handlePublish}>
            Publish
          </Button>
        </div>
      )}
    </Drawer>
  );
};

export default SettingsDrawer;
